import React, { useState, useEffect, useContext } from "react";
import { Form, Button } from "react-bootstrap";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { UserContext } from "shared/context/UserContext";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { LoadingSpinner } from "../../../components/layout/LoadingSpinner";
import "./ParentCompanySelectionForGroupUsers.css";

export default function ParentCompanySelectionForGroupUsers(props) {
  const {
    passedProps,
    setParentCompanyCallback,
    setMembersCallback,
    setPayersCallback,
    // Optional element rendered when, after loading, the selected organization
    // resolves to no accessible parent companies (i.e. the dropdown would be
    // empty / "NO DATA FOUND"). When not supplied, the empty dropdown is shown
    // as before. TPAR passes its "no access / not purchased" message here.
    noAccessContent,
  } = props;
  const [selectedParentCompany, setSelectedParentCompany] = useState("");
  const [membersAndRpids, setMembersAndRpids] = useState([]);
  const [uniqueRpids, setUniqueRpids] = useState([]);
  const [selectedPayers, setSelectedPayers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedLocations, setHasLoadedLocations] = useState(false);
  const userContext = useContext(UserContext);
  const urlPath = useLocation();

  useEffect(() => {
    if (userContext.currentOrganization.value) {
      setSelectedParentCompany("");
      setMembersAndRpids([]);
      setUniqueRpids([]);
      setSelectedPayers([]);
      setHasLoadedLocations(false);

      getUserLocation();
    }
  }, [userContext.currentOrganization.value]);

  async function getUserLocation() {
    let allLocationsParameters = {
      email: passedProps.user.email,
      selectedItem: userContext.currentOrganization.value,
      organizationType: userContext.currentOrganization.type,
      programSubscriptions: "Rec2.0",
    };
    let accessToken = passedProps.auth.getAccessToken();
    let currentRoute = urlPath.pathname;
    setIsLoading(true);
    let allLocationsResponse = await axios.get(
      `api/UserProgram/GetUserLocationsDetails`,
      {
        params: allLocationsParameters,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          RequestSrc: currentRoute,
        },
      }
    );

    let formattedLocations = allLocationsResponse.data.map((location) => {
      return {
        label: location.memberName + " (" + location.memberNcpdp + ")",
        value: location.memberMmid,
        ncpdp: location.memberNcpdp,
        mmid: location.memberMmid,
        rpid: location.memberRpid,
        parentCompanyName: location.parentCompanyName,
      };
    });

    let rpids = [];

    formattedLocations.forEach((item) => {
      if (!rpids.find((x) => x.rpid === item.rpid)) {
        rpids.push({
          rpid: item.rpid,
          parentCompanyName: item.parentCompanyName,
        });
      }
    });

    setMembersAndRpids(formattedLocations);
    setUniqueRpids(rpids);
    setHasLoadedLocations(true);
    setIsLoading(false);
  }

  const getMemberLocationsByRpid = (rpid) => {
    return membersAndRpids.filter((item) => item.rpid === rpid);
  };

  function formatDictionaryToArray(data) {
    return Object.keys(data).map((key) => {
      return {
        label: data[key],
        value: key,
      };
    });
  }

  async function getPayers(rpid) {
    const userInfo = await passedProps.user;
    const accessToken = await passedProps.auth.getAccessToken();

    let currentRoute = urlPath.pathname;

    let parameters = {
      email: userInfo.email,
      selectedItem: rpid,
      organizationType: "ParentCompany",
    };

    let header = {
      Authorization: `Bearer ${accessToken}`,
      RequestSrc: currentRoute,
    };
    setIsLoading(true);
    let payerResponse = await axios.get(
      `api/UserProgram/GetUserLocationsPayers`,
      {
        params: parameters,
        headers: header,
      }
    );

    let payers = formatDictionaryToArray(payerResponse.data);

    let sortedPayers = payers.sort((p1, p2) =>
      p1.label > p2.label ? 1 : p1.label < p2.label ? -1 : 0
    );

    setSelectedPayers(sortedPayers);
    setIsLoading(false);
  }

  const clickAction = (props) => {
    const selectedMembers = getMemberLocationsByRpid(
      selectedParentCompany.rpid
    );

    setParentCompanyCallback(selectedParentCompany);
    setMembersCallback(selectedMembers);
    setPayersCallback(selectedPayers);
  };

  const setSelectedParentCompanyAndGetPayers = async (parentCompany) => {
    setSelectedParentCompany(parentCompany);
    await getPayers(parentCompany.rpid);
  };

  const membersDisplayElement = () => {
    if (selectedParentCompany) {
      return (
        <div className="parent-company-selection-members-list">
          <h6>Members for {selectedParentCompany.parentCompanyName}</h6>
          <ul>
            {getMemberLocationsByRpid(selectedParentCompany.rpid).map(
              (item, index) => {
                return <li key={index}>{item.label}</li>;
              }
            )}
          </ul>
        </div>
      );
    } else {
      return <div>No parent company selected</div>;
    }
  };

  // After locations have loaded, if the selected organization resolves to no
  // accessible parent companies the dropdown would be empty ("NO DATA FOUND").
  // When the caller supplied a no-access message, show that instead.
  if (hasLoadedLocations && uniqueRpids.length === 0 && noAccessContent) {
    return (
      <div>
        <br />
        {noAccessContent}
      </div>
    );
  }

  return (
    <div>
      <br />
      <LoadingSpinner
        isDataLoading={isLoading}
        controlsName={"parentCompanySelectionForGroupUsers"}
      />
      <div className="bordered-control">
        <h4>Specify a Parent Company</h4>
        <hr />
        <Form.Control
          as={ComboBox}
          data={uniqueRpids || []}
          id="rpid"
          textField="parentCompanyName"
          allowCustom={false}
          value={selectedParentCompany}
          onChange={async (e) => {
            if (e.target.value) {
              // setSelectedParentCompany(e.target.value);
              await setSelectedParentCompanyAndGetPayers(e.target.value);
            } else setSelectedParentCompany(null);
          }}
        />
        <hr />

        {membersDisplayElement()}
        <hr />

        <Button
          className="inline-show-result-button"
          size="sm"
          type="submit"
          onClick={clickAction}
        >
          Click Here to Select Parent Company
        </Button>
      </div>
    </div>
  );
}
